import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
).toString();

type AccountingPdfJsPreviewProps = {
    /** Blob object URL from `URL.createObjectURL` */
    fileUrl: string;
    /** Shown while PDF.js parses the document */
    loadingLabel?: string;
    /** Shown when the document fails to load */
    errorLabel?: string;
};

/**
 * PDF preview via PDF.js (react-pdf), not the browser's built-in PDF iframe.
 * Avoids Chrome's embedded PDF viewer quirks (double load / flicker with blob URLs).
 */
export const AccountingPdfJsPreview: React.FC<AccountingPdfJsPreviewProps> = ({
    fileUrl,
    loadingLabel = 'Loading…',
    errorLabel = 'Could not load PDF.',
}) => {
    const wrapRef = useRef<HTMLDivElement | null>(null);
    const [pageWidth, setPageWidth] = useState(320);
    const [numPages, setNumPages] = useState(0);
    const [loadError, setLoadError] = useState<string | null>(null);

    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (!el) {
            return undefined;
        }
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width;
            if (w && w > 40) {
                setPageWidth(Math.floor(w));
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const onLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
        setLoadError(null);
        setNumPages(n);
    }, []);

    const onLoadError = useCallback((err: Error) => {
        setNumPages(0);
        setLoadError(err.message || 'error');
    }, []);

    useEffect(() => {
        setLoadError(null);
        setNumPages(0);
    }, [fileUrl]);

    return (
        <Box ref={wrapRef} sx={{ width: '100%', minHeight: 120 }}>
            {loadError ? (
                <Typography variant="body2" color="error" sx={{ p: 2 }}>
                    {errorLabel} {loadError ? `(${loadError})` : ''}
                </Typography>
            ) : (
                <Document
                    key={fileUrl}
                    file={fileUrl}
                    loading={
                        <Box sx={{ py: 2 }}>
                            <LinearProgress />
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                                {loadingLabel}
                            </Typography>
                        </Box>
                    }
                    onLoadSuccess={onLoadSuccess}
                    onLoadError={onLoadError}
                >
                    {numPages > 0
                        ? Array.from({ length: numPages }, (_, i) => (
                              <Box key={i + 1} sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                                  <Page pageNumber={i + 1} width={pageWidth} renderTextLayer renderAnnotationLayer />
                              </Box>
                          ))
                        : null}
                </Document>
            )}
        </Box>
    );
};
